import {Injectable, OnDestroy} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {nextFrame, observeMediaQuery, observeMutations, scrollTo} from '../helpers/dom';

const ALERTS_ANIMATION = 200;

@Injectable()
export class DomService implements OnDestroy {
    public docClickListeners = new Set<(event?: MouseEvent) => void>();
    private removeDocTapListener: () => void;
    private listenerWrapper: (event?: Event) => void;

    isOnboardingShowCreateApp = new BehaviorSubject(false);

    init() {
        document.body.classList.toggle('mobile-ios', this.isMobileIOS());
    }

    get isDesktop() {
        return !this.isMobile();
    }

    isMobile(): boolean {
        return /Android|webOS|BlackBerry/i.test(navigator.userAgent) || this.isMobileIOS();
    }

    isMobileIOS() {
        return !!navigator.platform && /iPad|iPhone|iPod/.test(navigator.platform);
    }

    ngOnDestroy() {
        document.removeEventListener('click', this.listenerWrapper);
        if (typeof this.removeDocTapListener === 'function') {
            this.removeDocTapListener();
            this.removeDocTapListener = null;
        }
        this.docClickListeners.clear();
    }

    media(mediaQuery: string): Observable<MediaQueryListEvent> {
        return observeMediaQuery(mediaQuery);
    }

    mutationObserver(target: Element, init: MutationObserverInit): Observable<Array<MutationRecord>> {
        return observeMutations(target, init);
    }

    onDocClick(listener: (event?: Event) => void): () => void {
        this.docClickListeners.add(listener);
        if (this.docClickListeners.size === 1) {
            this.listenerWrapper = (event: MouseEvent) => {
                this.docClickListeners.forEach(fn => fn(event));
            };
            document.addEventListener('click', this.listenerWrapper, {capture: true});
            this.removeDocTapListener = this.onTouchTap(document, event => {
                requestAnimationFrame(() => {
                    event.target.dispatchEvent(
                        new MouseEvent('click', {
                            bubbles: true,
                        })
                    );
                });
            });
        }
        return () => {
            this.docClickListeners.delete(listener);
            if (this.docClickListeners.size === 0) {
                document.removeEventListener('click', this.listenerWrapper);
                this.removeDocTapListener();
            }
        };
    }

    docClickStream(): Observable<Event> {
        return new Observable(sub => this.onDocClick(event => sub.next(event)));
    }

    onTouchTap(target: Element | Document, listener: (event?: TouchEvent) => void): () => void {
        let allowed = ['input', 'select', 'textarea', 'a'];
        let startTarget: HTMLElement;
        let endListener = (endEvent: TouchEvent) => {
            let {changedTouches} = endEvent;
            let endTarget: HTMLElement = (changedTouches.length === 1 && (changedTouches.item(0).target as HTMLElement)) || null;
            if (endTarget && startTarget === endTarget) {
                startTarget = null;
                if (!allowed.includes(endTarget.tagName.toLowerCase())) {
                    endEvent.preventDefault();
                }
                listener(endEvent);
            }
        };
        let startListener = (startEvent: TouchEvent) => {
            let {changedTouches} = startEvent;
            startTarget = (changedTouches.length === 1 && (changedTouches.item(0).target as HTMLElement)) || null;
        };
        let scrollListener = () => {
            startTarget = null;
        };
        target.addEventListener('touchstart', startListener);
        target.addEventListener('touchend', endListener, {passive: false, capture: false});
        document.addEventListener('scroll', scrollListener, {capture: true});
        return () => {
            target.removeEventListener('touchstart', startListener);
            target.removeEventListener('touchend', endListener, {capture: false});
            document.removeEventListener('scroll', scrollListener, {capture: true});
        };
    }

    scrollTopOffset() {
        return -document.body.querySelector('alerts').clientHeight - document.body.querySelector('header').clientHeight * 3;
    }

    async scrollToFirstInvalidElement(nativeElement: HTMLElement) {
        if (!nativeElement) {
            return;
        }
        // waite for next frame to let angular update view
        await nextFrame();
        // wait alerts animation
        await nextFrame(ALERTS_ANIMATION);

        let firstInvalidEl: HTMLElement = nativeElement.querySelector('.ng-invalid:not(form)');
        if (!firstInvalidEl) {
            firstInvalidEl = nativeElement.querySelector('form-errors:not(:empty)');
        }
        if (!firstInvalidEl) {
            return;
        }

        await scrollTo(firstInvalidEl, {
            // offset is needed to take into account header & alerts height
            offset: this.scrollTopOffset(),
            align: 'top',
            duration: 100,
        });
        if (firstInvalidEl) {
            firstInvalidEl.focus();
        }
    }

    scrollToElement(el: HTMLElement) {
        return scrollTo(el, {
            // offset is needed to take into account header & alerts height
            offset: this.scrollTopOffset(),
            align: 'top',
            duration: 100,
        });
    }

    preventBrowserSearch(elem) {
        if (!elem) {
            return () => {};
        }

        let keydownListener = e => {
            let ctrl = window.navigator.platform.match('Mac') ? e.metaKey : e.ctrlKey;
            let find = ctrl && (e.code === 'KeyF' || e.code === 'KeyG');
            let findWithShift = e.shiftKey && ctrl && e.code === 'KeyG';

            if (findWithShift || find) {
                e.preventDefault();
                elem.focus();
            }
        };
        document.addEventListener('keydown', keydownListener, false);

        return () => {
            document.removeEventListener('keydown', keydownListener, false);
        };
    }
}
